import images from '@assets/images/index'
import { Button } from '@components/ui'
import { Flex, Space } from 'antd'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Modal, Select } from '@suntex.fim/ui'

import { Gender } from '../../utils/enumAvatar'

import Avataaars from './AvataaarsRenderer'
import styles from './AvatarSelector.module.css'
import { AvatarSelectorProps, SelectedOptions, defaultOptions } from './types'
import {
  areObjectsEqual,
  getGenderSpecificRandomOption,
  getInitialOptions,
  getRandomValue,
  isValidGenderSpecificOption
} from './utils'

// Constants
const AVATAR_SIZE = 140
const MODAL_WIDTH = 680

/**
 * AvatarSelector - A React component for customizing and selecting avatars
 *
 * Features:
 * - Gender-specific avatar options
 * - Random avatar generation
 * - Real-time preview
 * - Accessible UI with proper ARIA labels
 * - Performance optimized with React.memo and useCallback
 *
 * @param props - The component props
 * @returns JSX element
 */
function AvatarSelector({
  onSelect,
  currentAvatar,
  defaultGender = Gender.Boy
}: AvatarSelectorProps) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedGender, setSelectedGender] = useState<Gender>(
    currentAvatar?.gender || defaultGender
  )
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptions>(
    () => {
      if (currentAvatar) {
        // Merge with initial options to ensure all properties exist
        return {
          ...getInitialOptions(currentAvatar.gender || defaultGender),
          ...currentAvatar
        }
      }
      return getInitialOptions(defaultGender)
    }
  )
  const [originalGender, setOriginalGender] = useState<Gender>(
    currentAvatar?.gender || defaultGender
  )
  // Store original state for cancel functionality
  const [originalOptions, setOriginalOptions] = useState<SelectedOptions>(
    () => {
      if (currentAvatar) {
        return {
          ...getInitialOptions(currentAvatar.gender || defaultGender),
          ...currentAvatar
        }
      }
      return getInitialOptions(defaultGender)
    }
  )

  const avatarRef = useRef<HTMLDivElement>(null)

  // Memoize style object to prevent recreations
  const avatarButtonStyle = useMemo(
    () => ({
      width: AVATAR_SIZE,
      height: AVATAR_SIZE
    }),
    []
  )

  // Memoize gender select options
  const genderOptions = useMemo(
    () => [
      { value: Gender.Boy, label: 'Boy' },
      { value: Gender.Girl, label: 'Girl' },
      { value: Gender.Other, label: 'Other' }
    ],
    []
  )

  // Memoize modal space props
  const modalSpaceProps = useMemo(
    () => ({
      direction: 'vertical' as const,
      style: { width: '100%' },
      size: 'large' as const
    }),
    []
  )

  // Update selected options when currentAvatar changes
  useEffect(() => {
    if (currentAvatar) {
      const newOptions = {
        ...getInitialOptions(currentAvatar.gender || defaultGender),
        ...currentAvatar
      }
      setSelectedOptions((prev) => {
        // Only update if options actually changed to prevent unnecessary re-renders
        if (!areObjectsEqual(prev, newOptions)) {
          return newOptions
        }
        return prev
      })
      setSelectedGender(currentAvatar.gender || defaultGender)
      // Update original states for cancel functionality
      setOriginalOptions(newOptions)
      setOriginalGender(currentAvatar.gender || defaultGender)
    }
  }, [currentAvatar, defaultGender])

  const randomizeOptions = useCallback(() => {
    setSelectedOptions((prev) => ({
      ...prev,
      topType: getGenderSpecificRandomOption('topType', selectedGender),
      hairColor: getRandomValue('hairColor'),
      accessoriesType: getRandomValue('accessoriesType'),
      facialHairType: 'Blank',
      clotheType: getGenderSpecificRandomOption('clotheType', selectedGender),
      clotheColor: getRandomValue('clotheColor'),
      eyeType: getRandomValue('eyeType'),
      eyebrowType: getRandomValue('eyebrowType'),
      mouthType: getRandomValue('mouthType'),
      skinColor: getRandomValue('skinColor')
    }))
  }, [selectedGender])

  const handleGenderChange = useCallback((gender: Gender) => {
    setSelectedGender(gender)
    const options = getInitialOptions(gender)
    setSelectedOptions(options)
  }, [])

  const handleOptionChange = useCallback(
    <T extends keyof SelectedOptions>(option: T, value: SelectedOptions[T]) => {
      // Validate gender-specific options
      if (
        !isValidGenderSpecificOption(option, value as string, selectedGender)
      ) {
        return
      }
      setSelectedOptions((prev) => ({
        ...prev,
        [option]: value
      }))
    },
    [selectedGender]
  )

  const handleSaveAvatar = useCallback(() => {
    onSelect({ ...selectedOptions, gender: selectedGender })
    setIsModalOpen(false)
  }, [selectedOptions, selectedGender, onSelect])

  // Memoize option label transformation
  const formatOptionLabel = useCallback((option: string) => {
    return String(option)
      .replace(/([A-Z])/g, ' $1')
      .trim()
  }, [])

  // Memoize category label transformation
  const formatCategoryLabel = useCallback((key: string) => {
    return key.replace(/([A-Z])/g, ' $1')
  }, [])

  // Memoize option rendering for better performance
  const renderSelectOption = useCallback(
    (option: string) => ({
      label: formatOptionLabel(option),
      value: option
    }),
    [formatOptionLabel]
  )

  const handleOpenModal = useCallback(() => {
    // Store current state as original for potential cancel
    setOriginalOptions(selectedOptions)
    setOriginalGender(selectedGender)
    setIsModalOpen(true)
  }, [selectedOptions, selectedGender])

  const handleCloseModal = useCallback(() => {
    // Restore original state when canceling
    setSelectedOptions(originalOptions)
    setSelectedGender(originalGender)
    setIsModalOpen(false)
  }, [originalOptions, originalGender])

  // Memoize avatar props to prevent unnecessary re-renders
  const avatarProps = useMemo(
    () => ({
      ...selectedOptions,
      avatarStyle: selectedOptions.avatarStyle || ('Circle' as const),
      clotheColor: selectedOptions.clotheColor,
      clotheType: selectedOptions.clotheType
    }),
    [selectedOptions]
  )

  // Memoize filterable options for better performance
  const renderableOptions = useMemo(
    () =>
      Object.entries(defaultOptions).filter(
        ([key]) => key !== 'facialHairType'
      ),
    []
  )

  return (
    <Flex vertical align="center" gap={16}>
      <button
        type="button"
        className={styles.avatarButton}
        style={avatarButtonStyle}
        aria-label="Customize avatar"
      >
        <div className={styles.avatarPreview}>
          <Avataaars
            {...avatarProps}
            style={{ width: '100%', height: '100%' }}
          />
          <images.EditCircle
            className={styles.avatarEditIcon}
            onClick={handleOpenModal}
          />
        </div>
      </button>

      <Modal
        title="Customize Your Avatar"
        open={isModalOpen}
        onOk={handleSaveAvatar}
        onCancel={handleCloseModal}
        width={MODAL_WIDTH}
        centered
      >
        <Space {...modalSpaceProps}>
          <Flex justify="center" align="center" gap={16}>
            <Flex justify="center" align="center">
              <div ref={avatarRef} className={styles.modalPreview}>
                <Avataaars
                  {...avatarProps}
                  style={{ width: '100%', height: '100%' }}
                />
              </div>
            </Flex>
            <Flex>
              <Button
                onClick={randomizeOptions}
                className={styles.randomButton}
              >
                Generate Random Avatar
              </Button>
            </Flex>
          </Flex>
          <Flex vertical gap={20} className={styles.avatarOptions}>
            <div>
              <h4 className={styles.optionLabel}>Gender</h4>
              <Select.One
                className={styles.selectInput}
                value={selectedGender}
                onChange={handleGenderChange}
                aria-label="Select gender"
                style={{ padding: '0 5px' }}
                options={genderOptions}
              />
            </div>

            {renderableOptions.map(([key, options]) => (
              <div key={key}>
                <h4 className={styles.optionLabel}>
                  {formatCategoryLabel(key)}
                </h4>
                <Select.One
                  className={styles.selectInput}
                  style={{ padding: '0 5px' }}
                  value={
                    selectedOptions[
                      key as keyof Omit<
                        SelectedOptions,
                        'facialHairType' | 'gender' | 'avatarStyle'
                      >
                    ]
                  }
                  onChange={(value: string) =>
                    handleOptionChange(
                      key as keyof Omit<
                        SelectedOptions,
                        'facialHairType' | 'gender' | 'avatarStyle'
                      >,
                      value
                    )
                  }
                  aria-label={`Select ${formatCategoryLabel(key)}`}
                  options={
                    Array.isArray(options)
                      ? options.map(renderSelectOption)
                      : undefined
                  }
                />
              </div>
            ))}
          </Flex>
        </Space>
      </Modal>
    </Flex>
  )
}

export default memo(AvatarSelector)
